/**
 * A drop-in for the native <select>, drawn in the studio's own colours.
 *
 * It takes exactly what a <select> takes — `value`, `onChange`, <option> and
 * <optgroup> children — so swapping one in is a rename, not a rewrite. The
 * browser's own list is the one part of a page no stylesheet reaches, and on
 * Windows it arrives as a grey system menu in the middle of a pink-and-ink app.
 *
 * `onChange` receives an event-shaped object whose `target.value` is the new
 * value, which is all any handler in this app reads. The list is portalled to
 * the body so a modal's or a table's overflow never clips it, and it opens
 * upwards when there is no room below.
 */

import { Check, ChevronDown } from 'lucide-react';
import {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type SelectHTMLAttributes
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';

interface Item {
  value: string;
  label: string;
  disabled: boolean;
  group?: string;
}

function text(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(text).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return text(node.props.children);
  return '';
}

function collect(children: ReactNode, group?: string, out: Item[] = []): Item[] {
  Children.forEach(children, (child) => {
    if (!isValidElement<{ children?: ReactNode; value?: unknown; disabled?: boolean; label?: string }>(child)) return;
    if (child.type === Fragment) {
      collect(child.props.children, group, out);
    } else if (child.type === 'optgroup') {
      collect(child.props.children, child.props.label, out);
    } else if (child.type === 'option') {
      const label = text(child.props.children);
      out.push({
        value: child.props.value === undefined ? label : String(child.props.value),
        label,
        disabled: child.props.disabled === true,
        ...(group === undefined ? {} : { group })
      });
    }
  });
  return out;
}

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> & {
  onChange?: ((e: ChangeEvent<HTMLSelectElement>) => void) | undefined;
};

export function Select({
  value,
  defaultValue,
  onChange,
  children,
  className,
  style,
  disabled,
  name,
  id,
  required,
  onBlur: _onBlur,
  ...rest
}: Props) {
  const items = collect(children);
  const [inner, setInner] = useState(String(defaultValue ?? items[0]?.value ?? ''));
  const current = value === undefined ? inner : String(value);
  const chosen = items.find((i) => i.value === current);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<{ left: number; top: number; width: number; up: boolean; max: number } | null>(
    null
  );
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const listId = useId();
  const typed = useRef({ text: '', at: 0 });

  const measure = useCallback(() => {
    const r = button.current?.getBoundingClientRect();
    if (r === undefined) return;
    const below = window.innerHeight - r.bottom;
    const up = below < 220 && r.top > below;
    setPlace({
      left: Math.min(r.left, window.innerWidth - Math.max(r.width, 180) - 8),
      top: up ? r.top : r.bottom,
      width: r.width,
      up,
      max: Math.max(160, Math.min(320, (up ? r.top : below) - 12))
    });
  }, []);

  const show = () => {
    if (disabled === true) return;
    const at = items.findIndex((i) => i.value === current);
    setActive(at < 0 ? 0 : at);
    measure();
    setOpen(true);
  };

  const pick = (item: Item | undefined) => {
    if (item === undefined || item.disabled) return;
    setOpen(false);
    button.current?.focus();
    if (item.value === current) return;
    if (value === undefined) setInner(item.value);
    const target = { value: item.value, name: name ?? '' };
    onChange?.({ target, currentTarget: target } as unknown as ChangeEvent<HTMLSelectElement>);
  };

  // Follow the trigger while the page scrolls or resizes; close on a click away.
  useLayoutEffect(() => {
    if (!open) return;
    const onMove = () => measure();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!button.current?.contains(t) && !list.current?.contains(t)) setOpen(false);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    document.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const step = (from: number, by: number) => {
    let i = from;
    for (let n = 0; n < items.length; n++) {
      i = Math.max(0, Math.min(items.length - 1, i + by));
      if (!items[i]?.disabled) return i;
    }
    return from;
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        show();
      }
      return;
    }
    if (e.key === 'ArrowDown') setActive((a) => step(a, 1));
    else if (e.key === 'ArrowUp') setActive((a) => step(a, -1));
    else if (e.key === 'Home') setActive(step(-1, 1));
    else if (e.key === 'End') setActive(step(items.length, -1));
    else if (e.key === 'Enter' || e.key === ' ') pick(items[active]);
    else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
      if (e.key === 'Tab') return;
    } else if (e.key.length === 1) {
      // Type the first letters of an option to jump to it, as a native list does.
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : '') + e.key.toLowerCase(), at: now };
      const hit = items.findIndex((i) => !i.disabled && i.label.toLowerCase().startsWith(typed.current.text));
      if (hit >= 0) setActive(hit);
    } else return;
    e.preventDefault();
  };

  let lastGroup: string | undefined;

  return (
    <>
      <button
        ref={button}
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-label={rest['aria-label']}
        aria-labelledby={rest['aria-labelledby']}
        title={rest.title}
        disabled={disabled}
        style={style}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKey}
        className={cn(
          'inline-flex cursor-pointer items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60',
          className
        )}
      >
        <span className={cn('min-w-0 truncate', chosen === undefined || chosen.value === '' ? 'opacity-70' : '')}>
          {chosen?.label ?? ''}
        </span>
        <ChevronDown
          aria-hidden
          className={cn('h-3.5 w-3.5 shrink-0 opacity-60 transition-transform', open && 'rotate-180')}
        />
      </button>
      {name !== undefined && <input type="hidden" name={name} value={current} required={required} />}
      {open &&
        place !== null &&
        createPortal(
          <ul
            ref={list}
            id={listId}
            role="listbox"
            style={{
              position: 'fixed',
              left: place.left,
              minWidth: Math.max(place.width, 160),
              maxHeight: place.max,
              ...(place.up ? { bottom: window.innerHeight - place.top + 4 } : { top: place.top + 4 })
            }}
            className="scrollbar-thin z-[400] overflow-auto rounded-xl border border-shell-border bg-shell p-1 shadow-pop"
          >
            {items.map((item, i) => {
              const heading = item.group !== undefined && item.group !== lastGroup ? item.group : undefined;
              lastGroup = item.group;
              const selected = item.value === current;
              return (
                <Fragment key={`${item.group ?? ''}:${item.value}`}>
                  {heading !== undefined && (
                    <li
                      role="presentation"
                      className="px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-shell-text opacity-70"
                    >
                      {heading}
                    </li>
                  )}
                  <li
                    data-i={i}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={item.disabled}
                    onPointerEnter={() => !item.disabled && setActive(i)}
                    onClick={() => pick(item)}
                    className={cn(
                      'flex cursor-pointer items-center justify-between gap-3 whitespace-nowrap rounded-lg px-2.5 py-2 text-sm sm:py-1.5',
                      i === active ? 'bg-brand-500/10 text-shell-text-active' : 'text-shell-text',
                      selected && 'font-semibold text-shell-text-active',
                      item.disabled && 'cursor-not-allowed opacity-40'
                    )}
                  >
                    {item.label || ' '}
                    {selected && <Check aria-hidden className="h-3.5 w-3.5 text-brand-500" />}
                  </li>
                </Fragment>
              );
            })}
          </ul>,
          document.body
        )}
    </>
  );
}
